import { Timestamp } from 'firebase/firestore';
// WARNING: This model is also used in Firebase Functions (functions/src/_shared/billing.model.ts)
// Keep interfaces and constants synchronized between frontend and backend!

export interface SchoolBreakdown {
  schoolId: string;
  name: string;
  registrations: number;
  newRegistrations: number;
  contracts: number;
  total: number;
}

export interface TierCalculation {
  count: number;
  price: number;
  cost: number;
}

export interface BillingSnapshot {
  date: Timestamp;
  totalBillable: number;
  totalRegistrations: number;
  totalNewRegistrations: number;
  totalContracts: number;
  monthlyAmount: number;
  avgPerStudent: number;
  currentTier: number;
  schools: SchoolBreakdown[];
  statusBreakdown: Record<string, number>;
  tierBreakdown: TierCalculation[];
  // Audit metadata
  createdOn?: Timestamp;
  createdBy?: string;
  createdByEmail?: string;
  lastModifiedBy?: string;
  lastModifiedByEmail?: string;
  lastModifiedSource?: string;
}

export const BILLING_TIERS = [
  { limit: 500, price: 0.69 },
  { limit: 2000, price: 0.59 },
  { limit: 5000, price: 0.49 },
  { limit: Infinity, price: 0.39 },
];

export interface MonthlyRegistrationStats {
  year: number;
  month: number;
  label: string;
  total: number;
  cumulative: number;
  monthlyCost: number;
  tierBreakdown: TierCalculation[];
}

// Persisted in Firestore collection `billingMonthlyStats/{YYYY-MM}`.
// Past months are written once with isFinalized=true and never re-touched even if
// students are deleted/migrated later. The current month is overwritten by the
// daily cron until the month rolls over.
export interface MonthlyBillingSnapshot extends MonthlyRegistrationStats {
  isFinalized: boolean;
  finalizedAt?: Timestamp;
  // Audit metadata
  createdOn?: Timestamp;
  createdBy?: string;
  createdByEmail?: string;
  lastModifiedBy?: string;
  lastModifiedByEmail?: string;
  lastModifiedSource?: string;
}

export function calculateTierPricing(totalStudents: number): {
  tiers: TierCalculation[];
  totalCost: number;
  currentTier: number;
} {
  if (totalStudents === 0) {
    return { tiers: [], totalCost: 0, currentTier: 0 };
  }

  const tiers: TierCalculation[] = [];
  let remaining = totalStudents;
  let prevLimit = 0;
  let totalCost = 0;

  for (const tier of BILLING_TIERS) {
    if (remaining <= 0) break;
    const bucketSize = tier.limit === Infinity ? remaining : tier.limit - prevLimit;
    const count = Math.min(remaining, bucketSize);
    const cost = Math.round(count * tier.price * 100) / 100;
    tiers.push({ count, price: tier.price, cost });
    totalCost += cost;
    remaining -= count;
    prevLimit = tier.limit === Infinity ? prevLimit + count : tier.limit;
  }

  return {
    tiers,
    totalCost: Math.round(totalCost * 100) / 100,
    currentTier: tiers.length,
  };
}

export const BILLABLE_STATUSES = [
  'registration_new',
  'registration_custom_start_date_pending',
  'registration_waitlist',
  'registration_contract_finalization',
  'registration_completed',
  'registration_cancellation_requested',
  'contract_active',
  'contract_renewal_pending',
  'contract_cancellation_requested',
  'contract_cancellation_approved',
];
